import { en } from './en'
import { me } from './me'
import { ru } from './ru'
import type { Dictionary, Lang } from './types'

export type { Dictionary, Lang, Localized } from './types'
export { LANGS } from './types'

export const dictionaries: Record<Lang, Dictionary> = { en, me, ru }

/** BCP-47 tags for <html lang>; Montenegrin uses the Latin-script Serbian tag browsers hyphenate well. */
export const htmlLang: Record<Lang, string> = { en: 'en', me: 'sr-Latn-ME', ru: 'ru' }

/** Locale used for number/time formatting. */
export const intlLocale: Record<Lang, string> = { en: 'en-GB', me: 'sr-Latn-ME', ru: 'ru-RU' }

export function isLang(value: unknown): value is Lang {
  return value === 'en' || value === 'me' || value === 'ru'
}

function lookup(source: unknown, path: string): unknown {
  let node: unknown = source
  for (const key of path.split('.')) {
    if (node === null || typeof node !== 'object') return undefined
    node = (node as Record<string, unknown>)[key]
  }
  return node
}

/**
 * Resolve a dotted key. In Cyber mode the `cyber.*` branch shadows matching keys,
 * e.g. `hero.title` → `cyber.hero.title`.
 */
export function translate(lang: Lang, path: string, cyber = false): string {
  const dict = dictionaries[lang]
  if (cyber) {
    const override = lookup(dict.cyber, path)
    if (typeof override === 'string') return override
  }
  const value = lookup(dict, path)
  if (typeof value === 'string') return value
  if (import.meta.env.DEV) console.warn(`[i18n] missing key "${path}" for ${lang}`)
  return path
}

export function guessLang(): Lang {
  const candidates = navigator.languages?.length ? navigator.languages : [navigator.language]
  for (const raw of candidates) {
    const tag = raw.toLowerCase()
    if (tag.startsWith('ru') || tag.startsWith('uk') || tag.startsWith('be')) return 'ru'
    if (/^(sr|me|cnr|bs|hr|sh)\b/.test(tag)) return 'me'
    if (tag.startsWith('en')) return 'en'
  }
  return 'en'
}
